import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Divider,
  Avatar,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useGetDashboardDataQuery,
  useGetListOfMembersQuery,
} from "@/features/bughurd/api";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardData } from "./interface";
import KanbanDashboard from "./KanbanDashboard";
import ScrumDashboard from "./ScrumDashboard";
import ChartWrapper from "./ChartWrapper";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { selectBoardState } from "@/features/bughurd/boardSelector";
import { ChangeCircle, Search, BarChartOutlined } from "@mui/icons-material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { alpha } from "@mui/material";
import {
  useLazyGetListOfProjectsQuery,
  useGetListOfProjectsQuery,
} from "@/features/bughurd/api";
import { addRecentProject, getRecentProjects } from "../../utils";
import {
  setSelectedItem,
  setViewType,
  setBacklogViewType,
  setStatusList,
} from "@/features/bughurd/slice";

const PMOHubDashboard: React.FC<{
  recharts?: any;
  parentData?: any;
}> = ({ recharts, parentData }) => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const boardState = useAppSelector(selectBoardState);

  const { projectDetails } = boardState;
  const boardType = projectDetails?.boardType;

  // Project switching states
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);
  const [shouldRefetchInitial, setShouldRefetchInitial] = useState(false);
  const projectListRef = useRef<HTMLDivElement | null>(null);

  const open = Boolean(anchorEl);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(projectSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [projectSearch]);

  // Fetch projects for the dropdown
  const [getProjectsLazy] = useLazyGetListOfProjectsQuery();
  const {
    data,
    isLoading: isLoadingProjects,
    isFetching,
  } = useGetListOfProjectsQuery(
    { searchName: debouncedSearch, page: 1, size: 5 },
    { skip: shouldRefetchInitial },
  );

  const { data: responseData, isLoading } = useGetDashboardDataQuery(
    { projectId: id },
    { refetchOnMountOrArgChange: true },
  );

  const { data: projectMembersData } = useGetListOfMembersQuery(
    { projectId: id, page: 1, size: 200, searchName: "" },
    { skip: !id },
  );
  const projectMembers = projectMembersData?.data?.decryptedData?.items || [];

  const dashboardData: DashboardData | undefined =
    responseData?.data?.decryptedData;
  const projectSummary = dashboardData?.project?.projectWiseSummary;
  const projectMetrics = dashboardData?.project?.projectMetricsFromSprint;
  const projectWorkloadMetrics = dashboardData?.project?.projectWorkloadMetrics;
  const sprints = dashboardData?.sprint || [];
  const memberStatistics = (responseData?.data?.decryptedData as any)
    ?.memberStatistics;

  const isKanbanBoard = boardType === "KANBAN";
  const chartData = useMemo(() => {
    return {
      chartWrapper: parentData?.chartWrapper || ChartWrapper,
      ...parentData,
    };
  }, [parentData]);

  // Initialize projects list
  useEffect(() => {
    if (data?.data?.decryptedData?.items) {
      setAllProjects(data.data.decryptedData.items);
      setProjectPage(1);
      const totalItems = data.data.decryptedData.totalCount || 0;
      setHasMoreProjects(data.data.decryptedData.items.length < totalItems);
    }
  }, [data]);

  useEffect(() => {
    if (!shouldRefetchInitial && data?.data?.decryptedData?.items) {
      setAllProjects(data.data.decryptedData.items);
      setProjectPage(1);
      const totalItems = data.data.decryptedData.totalCount || 0;
      setHasMoreProjects(data.data.decryptedData.items.length < totalItems);
    }
  }, [data, shouldRefetchInitial]);

  // Load more projects handler
  const loadMoreProjects = useCallback(async () => {
    if (isLoadingMoreProjects || !hasMoreProjects) return;

    setIsLoadingMoreProjects(true);
    try {
      const nextPage = projectPage + 1;
      const result = await getProjectsLazy({
        searchName: debouncedSearch,
        page: nextPage,
        size: 5,
      }).unwrap();

      if (result?.data?.decryptedData?.items) {
        const newProjects = result.data.decryptedData.items;
        setAllProjects((prev) => [...prev, ...newProjects]);
        setProjectPage(nextPage);
        const totalItems = result.data.decryptedData.totalCount || 0;
        const currentTotal = allProjects.length + newProjects.length;
        setHasMoreProjects(currentTotal < totalItems);
      }
    } catch (error) {
      console.error("Error loading more projects:", error);
    } finally {
      setIsLoadingMoreProjects(false);
    }
  }, [
    isLoadingMoreProjects,
    hasMoreProjects,
    projectPage,
    debouncedSearch,
    allProjects.length,
    getProjectsLazy,
  ]);

  // Scroll handler for project list
  const handleProjectListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isNearBottom && hasMoreProjects && !isLoadingMoreProjects) {
        loadMoreProjects();
      }
    },
    [hasMoreProjects, isLoadingMoreProjects, loadMoreProjects],
  );

  const getNonRecentProjects = () => {
    const recentIds = getRecentProjects().map((item) => item._id);
    return allProjects.filter((project) => !recentIds.includes(project._id));
  };

  // Handle project menu click
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setProjectSearch("");
  };

  // Handle project selection
  const handleProjectSelect = (project: any) => {
    handleClose();
    dispatch(setSelectedItem("Dashboard"));
    dispatch(setStatusList([]));

    // Save settings if needed
    let savedSettingData = JSON.parse(
      localStorage.getItem("taskDataKeep") || "{}",
    );
    if (savedSettingData?.assignees) {
      savedSettingData.assignees = [];
      localStorage.setItem("taskDataKeep", JSON.stringify(savedSettingData));
    }

    if (project?.id === "My Projects") {
      navigate("/defect-tracker/projects");
    } else {
      addRecentProject(project);
      navigate(`/defect-tracker/projects/${project._id}/${project.boardType === "SCRUM" ? "backlog" : "task-board"}`);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const emptyState = !dashboardData || !projectSummary;

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        pt: 2,
        pb: 2,
        px: { xs: 1, sm: 2, md: 3 },
        height: "100%",
        overflowY: "auto",
        overflowX: "clip",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#bbb",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#999",
        },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.4rem", sm: "1.7rem" },
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            {projectDetails?.name}
          </Typography>
          <Box
            onClick={handleClick}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <ChangeCircle sx={{ fontSize: "16px" }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {t("Change Project")}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "13px",
          }}
        >
          {isKanbanBoard
            ? t("Continuous flow board - Track all tasks")
            : `${sprints.length} ${t("Active Sprints")}`}
        </Typography>
      </Box>

      {emptyState ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
              maxWidth: 400,
              textAlign: "center",
            }}
          >
            <Box
              sx={(theme) => ({
                width: 80,
                height: 80,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
              })}
            >
              <BarChartOutlined
                sx={(theme) => ({
                  fontSize: 38,
                  color: alpha(theme.palette.primary.main, 0.45),
                })}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  color: "text.primary",
                  mb: 0.75,
                }}
              >
                {t("No Dashboard Data Yet")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.85rem",
                  lineHeight: 1.6,
                }}
              >
                {t(
                  "Once tasks are created and activity begins, your dashboard insights will appear here.",
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : isKanbanBoard ? (
        <KanbanDashboard
          projectSummary={projectSummary}
          projectWorkloadMetrics={projectWorkloadMetrics}
          parentData={chartData}
          totalUsers={projectDetails?.users?.length + 1 || 0}
          projectMembers={projectMembers}
          memberStatistics={memberStatistics}
        />
      ) : (
        <ScrumDashboard
          projectSummary={projectSummary}
          projectMetrics={projectMetrics}
          sprints={sprints}
          parentData={chartData}
          totalUsers={projectDetails?.users?.length + 1 || 0}
        />
      )}

      {/* Project Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "10px",
              boxShadow:
                "0px 4px 20px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.05)",
              mt: 0.5,
              width: 320,
              maxHeight: "none",
              overflow: "visible",
            },
          },
        }}
        MenuListProps={{
          sx: {
            p: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {t("Switch Project")}
          </Typography>
          <Typography
            onClick={() => {
              handleClose();
              navigate("/defect-tracker/projects");
            }}
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: theme.palette.primary.main,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {t("Show all projects")}
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder={t("Search projects...")}
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: "18px", color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "36px",
                borderRadius: "8px",
                fontSize: "13px",
                backgroundColor: theme.palette.action.hover,
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.divider,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: "1.5px",
                },
              },
            }}
          />
        </Box>

        <Divider />

        {/* Project list */}
        {!isLoadingProjects && !isFetching ? (
          <Box
            ref={projectListRef}
            onScroll={handleProjectListScroll}
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              px: 0.75,
              py: 0.75,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.action.disabled,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.text.disabled,
              },
            }}
          >
            {!projectSearch ? (
              <>
                {getRecentProjects().length > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "text.disabled",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        px: 1,
                        pt: 0.5,
                        pb: 0.5,
                      }}
                    >
                      {t("Recent")}
                    </Typography>
                    {getRecentProjects().map((project) => (
                      <MenuItem
                        key={`recent-${project._id}`}
                        onClick={() => handleProjectSelect(project)}
                        sx={{
                          borderRadius: "6px",
                          px: 1.5,
                          py: 1,
                          minHeight: "unset",
                          gap: 1.5,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor:
                              project?.icon || theme.palette.primary.main,
                          }}
                        >
                          {project?.projectKey?.slice(0, 2) ||
                            project?.name?.slice(0, 2)?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "text.primary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: 1.3,
                            }}
                          >
                            {project.name}
                          </Typography>
                          {project.projectKey && (
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "text.disabled",
                                lineHeight: 1.3,
                              }}
                            >
                              {project.projectKey}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {getNonRecentProjects().length > 0 && (
                      <Divider sx={{ my: 0.75 }} />
                    )}
                  </>
                )}

                {getNonRecentProjects().length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "text.disabled",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      px: 1,
                      pt: 0.5,
                      pb: 0.5,
                    }}
                  >
                    {t("All Projects")}
                  </Typography>
                )}
                {getNonRecentProjects().map((project) => (
                  <MenuItem
                    key={project._id}
                    onClick={() => handleProjectSelect(project)}
                    sx={{
                      borderRadius: "6px",
                      px: 1.5,
                      py: 1,
                      minHeight: "unset",
                      gap: 1.5,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor:
                          project?.icon || theme.palette.primary.main,
                      }}
                    >
                      {project?.projectKey?.slice(0, 2) ||
                        project?.name?.slice(0, 2)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                        }}
                      >
                        {project.name}
                      </Typography>
                      {project.projectKey && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "text.disabled",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.projectKey}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </>
            ) : (
              <>
                {allProjects.length > 0 ? (
                  allProjects.map((project) => (
                    <MenuItem
                      key={project._id}
                      onClick={() => handleProjectSelect(project)}
                      sx={{
                        borderRadius: "6px",
                        px: 1.5,
                        py: 1,
                        minHeight: "unset",
                        gap: 1.5,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor:
                            project?.icon || theme.palette.primary.main,
                        }}
                      >
                        {project?.projectKey?.slice(0, 2) ||
                          project?.name?.slice(0, 2)?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.primary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.name}
                        </Typography>
                        {project.projectKey && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "text.disabled",
                              lineHeight: 1.3,
                            }}
                          >
                            {project.projectKey}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography
                      sx={{ fontSize: "13px", color: "text.secondary" }}
                    >
                      {t("No projects found")}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Loading indicator for loading more projects */}
            {isLoadingMoreProjects && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={1.5}
                gap={1}
              >
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  {t("Loading more...")}
                </Typography>
              </Box>
            )}

            {/* Show message when no more projects to load */}
            {!hasMoreProjects && allProjects.length > 5 && (
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.disabled",
                  textAlign: "center",
                  py: 0.75,
                }}
              >
                {t("All projects loaded")}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={5}
          >
            <CircularProgress size={22} />
          </Box>
        )}

        <Divider />

        {/* Footer navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
          }}
        >
          <MenuItem
            onClick={() => handleProjectSelect({ id: "My Projects" })}
            sx={{
              borderRadius: "6px",
              px: 1,
              py: 0.5,
              minHeight: "unset",
              gap: 1,
              flex: 1,
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
                color: theme.palette.text.secondary,
              },
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <FolderOutlinedIcon />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {t("My Projects")}
            </Typography>
          </MenuItem>
          <MenuItem
            disabled
            sx={{
              borderRadius: "6px",
              px: 1,
              py: 0.5,
              minHeight: "unset",
              gap: 1,
              flex: 1,
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
              },
              "&.Mui-disabled": {
                opacity: 0.4,
              },
            }}
          >
            <DashboardCustomizeOutlinedIcon />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {t("Dashboard")}
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </Container>
  );
};

export default PMOHubDashboard;
